import { jsxs, jsx } from 'react/jsx-runtime';
import React__default, { useState, useRef, useEffect, useImperativeHandle } from 'react';
import uFuzzy from '../../../../../node_modules/@leeoniya/ufuzzy/dist/uFuzzy.js';
import { isHotkey } from 'is-hotkey';
import { ContextualMenuMeta } from '../ContextualMenuMeta.js';
import ContextualAddNodeMenu from './ContextualAddNodeMenu.js';
import ContextualLinkToItemMenu from './ContextualLinkToItemMenu.js';
import { EditorSelector } from '../../../Components/EditorSelector.js';
import ContextualModifyItemMenu from './ContextualModifyItemMenu.js';
import { useWindowEvent } from '@fabric-app/hooks/useWindowEvent';

const interceptedKeys = [
  "ArrowUp",
  "ArrowDown",
  "Enter",
  "Escape"
];
const fuzzySearchGroups = (groups, query, simple = false) => {
  const lowerQuery = query.toLowerCase();
  const newGroups = [];
  if (query.length > 0) {
    for (const group of groups) {
      let newGroup;
      if (simple) {
        newGroup = {
          ...group,
          items: group.items.filter((item) => item.label.toLowerCase().includes(lowerQuery))
        };
      } else {
        const itemLabels = group.items.map((item) => item.label);
        const fuzzy = new uFuzzy({
          unicode: true
        });
        const [idx] = fuzzy.search(itemLabels, query);
        newGroup = {
          ...group,
          items: group.items.filter((_, i) => idx?.includes(i))
        };
      }
      if (newGroup.items.length === 0) {
        continue;
      }
      newGroups.push(newGroup);
    }
    return newGroups;
  }
  return groups;
};
const ContextualMenuComponent = /* @__PURE__ */ React__default.forwardRef(({ editor, mobileTarget }, ref) => {
  const [state, setState] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const scopedRef = useRef(null);
  const contentRef = useRef(null);
  const selectionRef = useRef(null);
  const specRef = useRef(null);
  selectionRef.current = state?.selection ?? null;
  specRef.current = state?.spec ?? null;
  const queryRef = useRef(state?.query ?? null);
  queryRef.current = state?.query ?? null;
  const lastCandidateQueryRef = useRef(null);
  useEffect(() => {
    if (!specRef.current?.autoSelect && !selectionRef.current) return;
    if (!candidates.length || !specRef.current) return;
    const firstItem = candidates[0];
    if (!firstItem) return;
    if (lastCandidateQueryRef.current !== queryRef.current) {
      lastCandidateQueryRef.current = queryRef.current;
      setState((s) => s ? {
        ...s,
        selection: firstItem
      } : null);
    }
  }, [
    candidates
  ]);
  useImperativeHandle(ref, () => ({
    onMenuActive: (query, view, element, spec, mobile) => {
      setState({
        query,
        view,
        element,
        spec,
        selection: null,
        mobile: mobile ?? false
      });
      setCandidates([]);
    },
    onMenuInactive: () => {
      setState(null);
      lastCandidateQueryRef.current = null;
    },
    onMenuUpdated: (query, view, element, spec, mobile) => {
      setState({
        query,
        view,
        element,
        spec,
        selection: null,
        mobile: mobile ?? false
      });
    },
    onKeyDown: (e) => {
      if (!interceptedKeys.includes(e.key)) return;
      e.preventDefault();
      if (isHotkey("down", e)) {
        const currentIndex = candidates.findIndex((item) => item === state?.selection);
        const nextIndex = currentIndex + 1;
        if (nextIndex < candidates.length) {
          setState((s) => s ? {
            ...s,
            selection: candidates[nextIndex]
          } : null);
        } else {
          setState((s) => s ? {
            ...s,
            selection: candidates[0]
          } : null);
        }
      }
      if (isHotkey("up", e)) {
        const currentIndex = candidates.findIndex((item) => item === state?.selection);
        const nextIndex = currentIndex - 1;
        if (nextIndex >= 0) {
          setState((s) => s ? {
            ...s,
            selection: candidates[nextIndex]
          } : null);
        } else {
          setState((s) => s ? {
            ...s,
            selection: candidates[candidates.length - 1]
          } : null);
        }
      }
      if (isHotkey("enter", e)) {
        const currentItem = candidates.find((item) => item === state?.selection);
        if (!currentItem) return;
        scopedRef.current?.onSelect(currentItem);
      }
      if (isHotkey("escape", e)) {
        if (state?.view) {
          ContextualMenuMeta.reset(state.view);
        }
      }
      return true;
    }
  }));
  useEffect(() => {
    if (!state?.selection) return;
    const element = contentRef.current?.querySelector(`[data-keyboard-selected="true"]`);
    if (!element) return;
    element.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
  }, [
    state?.selection
  ]);
  useEffect(() => {
    if (!state?.mobile) return;
    contentRef.current?.focus();
  }, [
    state?.mobile
  ]);
  useWindowEvent("keydown", (e) => {
    if (!isHotkey("escape", e) || !state) return;
    e.preventDefault();
    e.stopPropagation();
    state.view.focus();
    ContextualMenuMeta.reset(state.view);
  });
  useEffect(() => {
    if (!state) return;
    const animationFrame = requestAnimationFrame(() => {
      const tr = state.view.state.tr;
      tr.scrollIntoView();
      state.view.dispatch(tr);
    });
    return () => cancelAnimationFrame(animationFrame);
  }, [
    state
  ]);
  if (!state) return null;
  return /* @__PURE__ */ jsxs(EditorSelector.Base, {
    target: state.element,
    mobileTarget,
    label: state.spec.name,
    overlay: state.spec.overlay,
    open: true,
    contentRef,
    onOpenChange: (open) => {
      if (!open) {
        ContextualMenuMeta.reset(state.view);
      }
    },
    children: [
      state.spec.name === "addNode" && /* @__PURE__ */ jsx(ContextualAddNodeMenu, {
        ref: scopedRef,
        state,
        onCandidatesChange: setCandidates,
        editor
      }),
      state.spec.name === "linkToItem" && /* @__PURE__ */ jsx(ContextualLinkToItemMenu, {
        ref: scopedRef,
        state,
        onCandidatesChange: setCandidates,
        editor
      }),
      state.spec.name === "modifyItem" && /* @__PURE__ */ jsx(ContextualModifyItemMenu, {
        ref: scopedRef,
        state,
        onCandidatesChange: setCandidates,
        editor
      })
    ]
  });
});
ContextualMenuComponent.displayName = "ContextualMenuComponent";
const ContextualMenu = /* @__PURE__ */ React__default.memo(ContextualMenuComponent);

export { ContextualMenu, fuzzySearchGroups };
//# sourceMappingURL=ContextualMenuComponent.js.map
